import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { Plus, Edit, Trash2, Save, X, Calendar, BookOpen } from 'lucide-react';

const TermManagement = () => {
    const [terms, setTerms] = useState([]);
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState('all');
    const [formData, setFormData] = useState({
        level_id: '',
        name: '',
        description: '',
        term_number: '',
        display_order: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedLevel !== 'all') {
            fetchTerms(selectedLevel);
        } else {
            fetchTerms();
        }
    }, [selectedLevel]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [levelsData, termsData] = await Promise.all([
                adminService.getLevels(),
                adminService.getTerms()
            ]);
            setLevels(levelsData.levels);
            setTerms(termsData.terms);
        } catch (err) {
            alert('Failed to fetch data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTerms = async (levelId = null) => {
        try {
            const params = levelId ? { level_id: levelId } : {};
            const response = await adminService.getTerms(params);
            setTerms(response.terms);
        } catch (err) {
            alert('Failed to fetch terms: ' + err.message);
        }
    };

    const handleOpenModal = (term = null) => {
        if (term) {
            setEditingTerm(term);
            setFormData({
                level_id: term.level_id,
                name: term.name,
                description: term.description || '',
                term_number: term.term_number,
                display_order: term.display_order
            });
        } else {
            setEditingTerm(null);
            setFormData({
                level_id: selectedLevel !== 'all' ? selectedLevel : (levels[0]?.id || ''),
                name: '',
                description: '',
                term_number: '',
                display_order: terms.length + 1
            });
        }
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTerm(null);
        setFormData({
            level_id: '',
            name: '',
            description: '',
            term_number: '',
            display_order: ''
        });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.level_id) {
            newErrors.level_id = 'Level is required';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Term name is required';
        }

        if (!formData.term_number || formData.term_number < 1) {
            newErrors.term_number = 'Valid term number is required';
        }

        if (!formData.display_order || formData.display_order < 1) {
            newErrors.display_order = 'Display order is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            if (editingTerm) {
                await adminService.updateTerm(editingTerm.id, formData);
                alert('Term updated successfully!');
            } else {
                await adminService.createTerm(formData);
                alert('Term created successfully!');
            }

            handleCloseModal();
            fetchTerms(selectedLevel !== 'all' ? selectedLevel : null);
        } catch (err) {
            alert('Failed to save term: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (termId, termName) => {
        if (!window.confirm(`Are you sure you want to delete "${termName}"? This will affect all associated lessons.`)) {
            return;
        }

        try {
            setLoading(true);
            await adminService.deleteTerm(termId);
            alert('Term deleted successfully!');
            fetchTerms(selectedLevel !== 'all' ? selectedLevel : null);
        } catch (err) {
            alert('Failed to delete term: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const groupedTerms = terms.reduce((acc, term) => {
        if (!acc[term.level_name]) {
            acc[term.level_name] = [];
        }
        acc[term.level_name].push(term);
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Term Management</h2>
                    <p className="text-gray-600">Manage terms within each level</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Level Filter */}
                    <select
                        value={selectedLevel}
                        onChange={(e) => setSelectedLevel(e.target.value)}
                        className="input-field"
                    >
                        <option value="all">All Levels</option>
                        {levels.map(level => (
                            <option key={level.id} value={level.id}>
                                {level.name}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={() => handleOpenModal()}
                        className="btn-primary flex items-center gap-2"
                        disabled={levels.length === 0}
                    >
                        <Plus className="w-5 h-5" />
                        Add Term
                    </button>
                </div>
            </div>

            {levels.length === 0 ? (
                <div className="card text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No levels found</p>
                    <p className="text-sm text-gray-500">Please create a level first before adding terms</p>
                </div>
            ) : terms.length === 0 ? (
                <div className="card text-center py-12">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No terms created yet</p>
                    <button
                        onClick={() => handleOpenModal()}
                        className="btn-primary mt-4"
                    >
                        Create First Term
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedTerms).map(([levelName, levelTerms]) => (
                        <div key={levelName} className="card">
                            <h3 className="text-xl font-bold text-purple-900 mb-4 pb-3 border-b">
                                {levelName}
                            </h3>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {levelTerms.map(term => (
                                    <div
                                        key={term.id}
                                        className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-5 hover:shadow-lg transition-all"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">📅</span>
                                                <div>
                                                    <h4 className="font-bold text-lg">{term.name}</h4>
                                                    <p className="text-xs text-gray-600">Term {term.term_number}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleOpenModal(term)}
                                                    className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                                                >
                                                    <Edit className="w-4 h-4 text-blue-600" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(term.id, term.name)}
                                                    className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-600" />
                                                </button>
                                            </div>
                                        </div>

                                        {term.description && (
                                            <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                                                {term.description}
                                            </p>
                                        )}

                                        <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {term.lessons_count || 0} Lessons
                      </span>
                                            <span className="text-xs text-gray-500">
                        Order: {term.display_order}
                      </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
                            <h2 className="text-2xl font-bold">
                                {editingTerm ? 'Edit Term' : 'Create New Term'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Level Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Level <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="level_id"
                                    value={formData.level_id}
                                    onChange={handleChange}
                                    className={`input-field ${errors.level_id ? 'border-red-500' : ''}`}
                                    disabled={editingTerm}
                                >
                                    <option value="">Select a level</option>
                                    {levels.map(level => (
                                        <option key={level.id} value={level.id}>
                                            {level.name} (Age {level.min_age}-{level.max_age})
                                        </option>
                                    ))}
                                </select>
                                {errors.level_id && (
                                    <p className="text-red-600 text-sm mt-1">{errors.level_id}</p>
                                )}
                            </div>

                            {/* Term Name */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Term Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`input-field ${errors.name ? 'border-red-500' : ''}`}
                                    placeholder="e.g., Term 1: Introduction to Science"
                                />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    className="input-field"
                                    placeholder="Brief description of what students will learn in this term..."
                                />
                            </div>

                            {/* Term Number & Display Order */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Term Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="term_number"
                                        value={formData.term_number}
                                        onChange={handleChange}
                                        min="1"
                                        className={`input-field ${errors.term_number ? 'border-red-500' : ''}`}
                                        placeholder="1"
                                    />
                                    {errors.term_number && (
                                        <p className="text-red-600 text-sm mt-1">{errors.term_number}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Display Order <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="display_order"
                                        value={formData.display_order}
                                        onChange={handleChange}
                                        min="1"
                                        className={`input-field ${errors.display_order ? 'border-red-500' : ''}`}
                                        placeholder="1"
                                    />
                                    {errors.display_order && (
                                        <p className="text-red-600 text-sm mt-1">{errors.display_order}</p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <p className="text-sm text-blue-900">
                                    <strong>Note:</strong> Term number is used for identification (Term 1, Term 2, etc.),
                                    while display order determines how terms appear in the curriculum.
                                </p>
                            </div>

                            {/* Preview */}
                            {formData.name && formData.level_id && (
                                <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                                    <p className="text-sm font-medium text-purple-900 mb-2">Preview:</p>
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl">📅</span>
                                        <div>
                                            <p className="font-bold text-lg">{formData.name}</p>
                                            <p className="text-sm text-gray-600">
                                                {levels.find(l => l.id === parseInt(formData.level_id))?.name || 'Level'} •
                                                Term {formData.term_number || '?'}
                                            </p>
                                            {formData.description && (
                                                <p className="text-xs text-gray-500 mt-1">{formData.description}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {editingTerm ? 'Update Term' : 'Create Term'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TermManagement;